// =============================================================================
// MODEL DEFINITIONS
// =============================================================================

/**
 * A single LLM model entry.
 *
 * `inputPrice` and `outputPrice` are USD per million tokens.
 * `contextWindow` is the maximum number of tokens supported.
 */
export interface ModelDefinition {
  value: string;
  label: string;
  contextWindow: number;
  inputPrice: number;
  outputPrice: number;
}

export const ALL_AVAILABLE_MODELS = {
  'google-ai': [
    { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro', contextWindow: 1_000_000, inputPrice: 2, outputPrice: 12 },
    { value: 'gemini-3-flash-preview', label: 'Gemini 3 Flash', contextWindow: 1_000_000, inputPrice: 0.5, outputPrice: 3 },
    { value: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash Lite', contextWindow: 1_000_000, inputPrice: 0.25, outputPrice: 1.5 },
  ],
  'anthropic': [
    { value: 'claude-opus-4-7', label: 'Claude Opus 4.7', contextWindow: 1_000_000, inputPrice: 5, outputPrice: 25 },
    { value: 'claude-opus-4-5', label: 'Claude Opus 4.5', contextWindow: 200_000, inputPrice: 5, outputPrice: 25 },
    { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6', contextWindow: 1_000_000, inputPrice: 3, outputPrice: 15 },
    { value: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5', contextWindow: 200_000, inputPrice: 3, outputPrice: 15 },
    { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5', contextWindow: 200_000, inputPrice: 1, outputPrice: 5 },
  ],
  'openai': [
    { value: 'gpt-5.5', label: 'GPT 5.5', contextWindow: 1_000_000, inputPrice: 5, outputPrice: 30 },
    { value: 'gpt-5.4', label: 'GPT 5.4', contextWindow: 1_000_000, inputPrice: 2.5,   outputPrice: 15 },
    { value: 'gpt-5.4-mini', label: 'GPT 5.4 Mini', contextWindow: 400_000, inputPrice: 0.75, outputPrice: 4.5 },
  ],
  'xai': [
    { value: 'grok-4', label: 'Grok 4', contextWindow: 256_000, inputPrice: 3, outputPrice: 15 },
    { value: 'grok-4.3', label: 'Grok 4.3', contextWindow: 1_000_000, inputPrice: 1.25, outputPrice: 2.5 },
    { value: 'grok-4-1-fast-reasoning', label: 'Grok 4.1 Fast', contextWindow: 2_000_000, inputPrice: 0.2, outputPrice: 0.5 },
    { value: 'grok-4-1-fast-non-reasoning', label: 'Grok 4.1 NoReason', contextWindow: 2_000_000, inputPrice: 0.2, outputPrice: 0.5 },
  ],
  'openrouter': [
    { value: 'moonshotai/kimi-k2-thinking', label: 'Kimi K2 Thinking', contextWindow: 256_000, inputPrice: 0.45, outputPrice: 2.25 },
    { value: 'z-ai/glm-5.1', label: 'GLM 5.1 Thinking', contextWindow: 1_000_000, inputPrice: 0.7, outputPrice: 3.5 },
    { value: 'z-ai/glm-5', label: 'GLM 5', contextWindow: 200_000, inputPrice: 0.35, outputPrice: 1.75 },
    { value: 'aion-labs/aion-2.0', label: 'Aion 2', contextWindow: 128_000, inputPrice: 0.32, outputPrice: 1.6 },
    { value: 'minimax/minimax-m2.5', label: 'Minimax M2.5', contextWindow: 200_000, inputPrice: 0.22, outputPrice: 1.1 },
    { value: 'nousresearch/hermes-4-405b', label: 'Nous Hermes 4', contextWindow: 128_000, inputPrice: 0.2, outputPrice: 1 },
    { value: 'deepseek/deepseek-v4-pro', label: 'Deepseek V4 Pro', contextWindow: 1_000_000, inputPrice: 0.174, outputPrice: 0.87 },
    { value: 'deepseek/deepseek-v3.2', label: 'Deepseek V3.2', contextWindow: 160_000, inputPrice: 0.08, outputPrice: 0.4 },
    { value: 'deepseek/deepseek-v4-flash', label: 'Deepseek V4 Flash', contextWindow: 1_000_000, inputPrice: 0.056, outputPrice: 0.28 },
    { value: 'openai/gpt-oss-120b', label: 'GPT-OSS 120b', contextWindow: 128_000, inputPrice: 0.05, outputPrice: 0.25 },
    { value: 'arcee-ai/trinity-large-preview', label: 'Trinity Large Preview', contextWindow: 128_000, inputPrice: 0.15, outputPrice: 0.45 },
  ],
  'mistral': [
    { value: 'mistral-large-latest', label: 'Mistral Large 3', contextWindow: 256_000, inputPrice: 0.3, outputPrice: 1.5 },
    { value: 'mistralai/mistral-medium-3-5', label: 'Mistral Medium 3.5', contextWindow: 256_000, inputPrice: 1.5, outputPrice: 7.5 },
    { value: 'labs-mistral-small-creative', label: 'Mistral Small Creative', contextWindow: 32_000, inputPrice: 0.06, outputPrice: 0.3 },
  ],
} satisfies Record<string, ModelDefinition[]>;

export const DEFAULT_GOOGLE_MODELS = ALL_AVAILABLE_MODELS['google-ai'];
export const DEFAULT_ANTHROPIC_MODELS = ALL_AVAILABLE_MODELS['anthropic'];
export const DEFAULT_OPENAI_MODELS = ALL_AVAILABLE_MODELS['openai'];
export const DEFAULT_XAI_MODELS = ALL_AVAILABLE_MODELS['xai'];
export const DEFAULT_OPENROUTER_MODELS = ALL_AVAILABLE_MODELS['openrouter'];
export const DEFAULT_MISTRAL_MODELS = ALL_AVAILABLE_MODELS['mistral'];

/**
 * FAL image generation models for text-to-image.
 * These are accessed via the FAL provider with normalized synthetic parameters.
 */
export const FAL_IMAGE_MODELS = [
  { value: 'fal-ai/z-image/turbo', label: 'Z-Image Turbo ($0.005/MP)' },
  { value: 'fal-ai/z-image/base', label: 'Z-Image Base ($0.005/MP)' },
  { value: 'fal-ai/flux-2', label: 'FLUX 2 ($0.01/MP)' },
  { value: 'fal-ai/flux-2/turbo', label: 'FLUX 2 Turbo ($0.004/img)' },
  { value: 'fal-ai/nano-banana', label: 'Nano Banana ($0.04/img)' },
  { value: 'fal-ai/nano-banana-pro', label: 'Nano Banana Pro ($0.15/$0.30/img)' },
  { value: 'fal-ai/nano-banana-2', label: 'Nano Banana 2 ($0.04/img)' },
  { value: 'rundiffusion-fal/juggernaut-flux/pro', label: 'Juggernaut FLUX Pro ($0.05/MP)' },
  { value: 'fal-ai/gpt-image-1.5', label: 'GPT Image 1.5 ($0.01/$0.05/$0.20/img)' },
  { value: 'openai/gpt-image-2', label: 'GPT Image 2 (Alpha)' },
  { value: 'fal-ai/qwen-image-2512', label: 'Qwen Image 2512 ($0.02/MP)' },
  { value: 'xai/grok-imagine-image', label: 'Grok Imagine ($0.022/img)' },
] as const;

/**
 * FAL models that support multi-reference image generation.
 * These models accept reference images to maintain visual consistency across characters.
 */
export const FAL_MULTI_REFERENCE_MODELS = [
  { value: 'fal-ai/nano-banana/edit', label: 'Nano Banana ($0.04/img) - Multi-Reference' },
  { value: 'fal-ai/nano-banana-pro/edit', label: 'Nano Banana Pro ($0.15/img) - Multi-Reference' },
  { value: 'fal-ai/nano-banana-2/edit', label: 'Nano Banana 2 ($0.04/img) - Multi-Reference' },
  { value: 'fal-ai/flux-2-pro/edit', label: 'FLUX 2 Pro Edit ($0.03/MP) - Multi-Reference' },
  { value: 'fal-ai/flux-2/turbo/edit', label: 'FLUX 2 Turbo Edit ($0.004/img) - Multi-Reference' },
  { value: 'xai/grok-imagine-image/edit', label: 'Grok Imagine ($0.022/img) - Multi-Reference' },
  { value: 'fal-ai/gpt-image-1.5/edit', label: 'GPT Image 1.5 Edit ($0.01/$0.05/$0.20/img) - Multi-Reference' },
  { value: 'openai/gpt-image-2/edit', label: 'GPT Image 2 Edit (Alpha) - Multi-Reference' },
] as const;

// =============================================================================
// PROVIDER DISPLAY NAMES
// =============================================================================

/**
 * Human-readable display names for AI providers.
 * Used across the UI for consistent provider labeling.
 */
export const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  'google-ai': 'Google (Gemini)',
  'anthropic': 'Anthropic',
  'openai': 'OpenAI',
  'xai': 'xAI',
  'openrouter': 'OpenRouter',
  'mistral': 'Mistral',
  'fal-ai': 'FAL',
};

/**
 * Get the display name for a provider ID
 */
export function getProviderDisplayName(provider: string): string {
  return PROVIDER_DISPLAY_NAMES[provider] ?? provider;
}

// =============================================================================
// MODEL HELPER FUNCTIONS
// =============================================================================

function formatContextWindow(tokens: number): string {
  if (tokens >= 1_000_000) {
    const m = tokens / 1_000_000;
    return `${Number.isInteger(m) ? m : m.toFixed(1)}M`;
  }
  return `${Math.round(tokens / 1000)}k`;
}

function formatPricePair(inputPrice: number, outputPrice: number): string {
  if (inputPrice === 0 && outputPrice === 0) return 'Free';
  return `$${inputPrice}/$${outputPrice}`;
}

/**
 * Compose a rich display label for a model: "Name (1M, $5/$25)".
 */
export function formatModelLabel(model: ModelDefinition): string {
  return `${model.label} (${formatContextWindow(model.contextWindow)}, ${formatPricePair(model.inputPrice, model.outputPrice)})`;
}

function findModel(modelValue: string): ModelDefinition | undefined {
  // Openrouter values include a slash (e.g. 'moonshotai/kimi-k2-thinking'),
  // so when prefixed it looks like 'openrouter/moonshotai/kimi-k2-thinking'.
  if (modelValue.startsWith('openrouter/')) {
    const modelName = modelValue.slice('openrouter/'.length);
    return ALL_AVAILABLE_MODELS['openrouter'].find(m => m.value === modelName);
  }

  const slash = modelValue.indexOf('/');
  if (slash === -1) return undefined;
  const provider = modelValue.slice(0, slash);
  const modelName = modelValue.slice(slash + 1);
  const models = ALL_AVAILABLE_MODELS[provider as keyof typeof ALL_AVAILABLE_MODELS];
  return models?.find(m => m.value === modelName);
}

/**
 * Get the rich display label for a fully-qualified model ID
 * (e.g. "anthropic/claude-opus-4-7"). Falls back to the raw ID if unknown.
 */
export function getModelLabel(modelValue: string): string {
  const model = findModel(modelValue);
  return model ? formatModelLabel(model) : modelValue;
}

// =============================================================================
// PRICE-BASED GROUPING
// =============================================================================

export interface FlatModel {
  /** Full model identifier: "provider/model-value" */
  id: string;
  label: string;
  contextWindow: number;
  inputPrice: number;
  outputPrice: number;
}

export type PriceTier = '>$10' | '$1 - $9.99' | '<$1';

export const PRICE_TIERS: { key: PriceTier; label: string; min: number; max: number }[] = [
  { key: '>$10', label: '>$10', min: 10, max: Infinity },
  { key: '$1 - $9.99', label: '$1 - $9.99', min: 1, max: 10 },
  { key: '<$1', label: '<$1', min: -Infinity, max: 1 },
];

/**
 * Get all models flattened into a single list with provider-prefixed IDs,
 * grouped by output-price tier. Models within each tier are sorted by output
 * price descending.
 */
export function getModelsByPriceTier(): Record<PriceTier, FlatModel[]> {
  const allModels: FlatModel[] = [];

  for (const [provider, models] of Object.entries(ALL_AVAILABLE_MODELS)) {
    for (const model of models) {
      allModels.push({
        id: `${provider}/${model.value}`,
        label: formatModelLabel(model),
        contextWindow: model.contextWindow,
        inputPrice: model.inputPrice,
        outputPrice: model.outputPrice,
      });
    }
  }

  const grouped: Record<PriceTier, FlatModel[]> = {
    '>$10': [],
    '$1 - $9.99': [],
    '<$1': [],
  };

  for (const model of allModels) {
    const tier = PRICE_TIERS.find(t => model.outputPrice >= t.min && model.outputPrice < t.max);
    if (tier) {
      grouped[tier.key].push(model);
    }
  }

  for (const tier of Object.values(grouped)) {
    tier.sort((a, b) => b.outputPrice - a.outputPrice);
  }

  return grouped;
}
